import { useToast } from "hooks/useToasts";
import { useEffect } from "react";
import type { FieldPath } from "react-hook-form";

import type { LPQuoteInput } from "../../types/Rater.types";
import { removeFormsWithToast } from "../utils/removeFormsWithToast";
import { BusinessRulesArgs } from "./useBusinessRules";

const requiredFormByOccupancy = {
  S: "SLP01",
  V: "VAC01",
  STR: "STR01",
} as const;
const automatedOccupancyForms = Object.values(requiredFormByOccupancy);

export function getRequiredOccupancyForm(occupancyType?: string) {
  return requiredFormByOccupancy[occupancyType as keyof typeof requiredFormByOccupancy];
}

export function isAutomatedOccupancyForm(formName: string) {
  return automatedOccupancyForms.includes(formName as (typeof automatedOccupancyForms)[number]);
}

function getFormDisplayName(formsData: BusinessRulesArgs["formsData"], formName: string) {
  const formData = formsData[formName];
  return formData?.displayFormName ?? formData?.formTitle ?? formName;
}

type OccupancyFormToast = {
  title: string;
  color: "primary" | "danger";
  text: string;
};

function getOccupancyFormToast(
  occupancyType: string | undefined,
  requiredForm: string | undefined,
  formsData: BusinessRulesArgs["formsData"],
): OccupancyFormToast {
  if (requiredForm) {
    return {
      title: "Occupancy Type form changed",
      color: "primary",
      text: `Your Occupancy Type form changed to ${getFormDisplayName(formsData, requiredForm)}.`,
    };
  }

  if (!occupancyType) {
    return {
      title: "Occupancy Type form removed",
      color: "danger",
      text: "Your Occupancy Type form was removed because no occupancy type is selected.",
    };
  }

  return {
    title: "Occupancy Type form removed",
    color: "primary",
    text: "Your Occupancy Type form was removed because the selected occupancy type does not require one.",
  };
}

export function useOccupancyRules({
  locationValues,
  tabItem,
  forms,
  formsData,
  enabled,
  appendForm,
  removeForm,
  setValue,
  htmlIdGenerator,
}: BusinessRulesArgs) {
  const { occupancyType, perils } = locationValues;

  const isVacantOccupancy = occupancyType === "V";
  const isSeasonalOccupancy = occupancyType === "S";
  const isShortTermRentalOccupancy = occupancyType === "STR";

  // {START: Add or remove required occupancy forms}
  const { addToast } = useToast();
  useEffect(() => {
    if (!enabled) return;

    const requiredForm = getRequiredOccupancyForm(occupancyType);

    const formsToRemove = forms
      .map((form, index) => ({ form, index }))
      .filter(({ form }) => {
        return isAutomatedOccupancyForm(form.name) && form.name !== requiredForm;
      });
    removeFormsWithToast({
      addToast,
      removeForm,
      formsData,
      formsToRemove,
      reason: "due to a change in occupancy type",
      toast: getOccupancyFormToast(occupancyType, requiredForm, formsData),
    });

    if (requiredForm && !forms.some((form) => form.name === requiredForm)) {
      appendForm({
        formID: htmlIdGenerator()(),
        name: requiredForm,
        fields: undefined,
      });
    }
  }, [occupancyType, forms, appendForm, removeForm, htmlIdGenerator, formsData, enabled, addToast]);
  // {END: Add or remove required occupancy forms}

  // {START: Default to FL1R when Occupancy Type is Vacant}
  useEffect(() => {
    if (!enabled) return;

    const perilPath = `locations.${tabItem}.perils` as FieldPath<LPQuoteInput>;

    if (!occupancyType) return;

    if (occupancyType === "V" && perils !== "FL1R") {
      setValue(perilPath, "FL1R", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [occupancyType, perils, tabItem, setValue, enabled]);
  // {END: Default to FL1R when Occupancy Type is Vacant}

  function isLockedOccupancyForm(formName: string) {
    return (
      (isSeasonalOccupancy && formName === "SLP01") ||
      (isVacantOccupancy && formName === "VAC01") ||
      (isShortTermRentalOccupancy && formName === "STR01")
    );
  }

  return {
    isVacantOccupancy,
    isSeasonalOccupancy,
    isShortTermRentalOccupancy,
    isLockedOccupancyForm,
  };
}
